import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, EmptyState, ErrorState, PageHeader, Pill, Skeleton } from "../components/ui";
import {
  fetchCompletedAiInsights,
  fetchCreditServices,
  fetchCreditsBalance,
  fetchDietitianVisitHistory,
  fetchDietitianPendingVisits,
  fetchMyBlogs,
  fetchPantryItems,
  fetchScans,
} from "../lib/api";
import { assetUrl } from "../lib/assets";
import { trackEvent } from "../lib/analytics";
import { useShellSession } from "../hooks/useShellSession";
import type { PantryScanSubmission } from "../types";

function formatRelative(dateRaw?: string): string {
  if (!dateRaw) {
    return "Unknown";
  }
  const date = new Date(dateRaw);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function scanLabel(scan: PantryScanSubmission): string {
  if (scan.title && scan.title.trim()) {
    return scan.title;
  }
  return `Pantry scan · ${formatRelative(scan.created_at)}`;
}

function toCreditNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function HomePage() {
  const { session } = useShellSession();
  const isDietitian = Boolean(session.user?.isDietitian);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scans, setScans] = useState<PantryScanSubmission[]>([]);
  const [pantryCount, setPantryCount] = useState(0);
  const [scanCredits, setScanCredits] = useState<number | null>(null);
  const [consultCredits, setConsultCredits] = useState<number | null>(null);
  const [insightCost, setInsightCost] = useState<number | null>(null);
  const [pendingVisitCount, setPendingVisitCount] = useState(0);
  const [confirmedVisitCount, setConfirmedVisitCount] = useState(0);
  const [completedInsightCount, setCompletedInsightCount] = useState(0);
  const [draftPostCount, setDraftPostCount] = useState(0);

  useEffect(() => {
    let mounted = true;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        if (isDietitian) {
          const [pendingVisits, historyVisits, completedInsights, myBlogs, creditsPayload] = await Promise.all([
            fetchDietitianPendingVisits({ limit: 100, offset: 0 }),
            fetchDietitianVisitHistory({ limit: 100, offset: 0 }),
            fetchCompletedAiInsights(7, null, 100),
            fetchMyBlogs(100, 0),
            fetchCreditsBalance().catch(() => null),
          ]);
          if (!mounted) {
            return;
          }
          setPendingVisitCount((pendingVisits ?? []).length);
          setConfirmedVisitCount((historyVisits ?? []).filter((visit) => (visit.status || "").toUpperCase() === "CONFIRMED").length);
          setCompletedInsightCount((completedInsights.items ?? []).length);
          setDraftPostCount(
            (myBlogs.items ?? []).filter((blog) => (blog.status || "").toUpperCase() === "DRAFT").length,
          );
          setScanCredits(toCreditNumber(creditsPayload?.scan_credits ?? creditsPayload?.credits_available));
          setConsultCredits(toCreditNumber(creditsPayload?.dietitian_credits ?? creditsPayload?.consult_credits));
          setScans([]);
          return;
        }

        const [scansPayload, pantryPayload, creditsPayload, servicesPayload] = await Promise.all([
          fetchScans(),
          fetchPantryItems(50),
          fetchCreditsBalance(),
          fetchCreditServices(),
        ]);

        if (!mounted) {
          return;
        }

        setScans(Array.isArray(scansPayload) ? scansPayload : []);
        setPantryCount((pantryPayload.items ?? []).length);
        setScanCredits(toCreditNumber(creditsPayload.scan_credits ?? creditsPayload.credits_available));
        setConsultCredits(toCreditNumber(creditsPayload.dietitian_credits ?? creditsPayload.consult_credits));
        const insightService = servicesPayload.services?.find((service) => service.key === "pantry_analysis");
        setInsightCost(insightService?.cost ?? null);
      } catch (err) {
        if (!mounted) {
          return;
        }
        setError(err instanceof Error ? err.message : "Unable to load home dashboard.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      mounted = false;
    };
  }, [isDietitian]);

  const latestScan = scans[0];

  const statCards = useMemo(
    () => [
      { title: "Pantry items", value: pantryCount.toString(), subtitle: "Items indexed" },
      { title: "Scan credits", value: scanCredits === null ? "-" : `${scanCredits}`, subtitle: "Available" },
      { title: "Dietitian credits", value: consultCredits === null ? "-" : `${consultCredits}`, subtitle: "Available" },
      { title: "Insight cost", value: insightCost === null ? "-" : `${insightCost} credits`, subtitle: "Per run" },
    ],
    [consultCredits, insightCost, pantryCount, scanCredits],
  );

  const dietitianCards = useMemo(
    () => [
      { title: "Pending requests", value: pendingVisitCount.toString(), subtitle: "Need slot triage" },
      { title: "Confirmed appointments", value: confirmedVisitCount.toString(), subtitle: "Ready for follow-up" },
      { title: "Dietitian credits", value: consultCredits === null ? "-" : `${consultCredits}`, subtitle: "Available" },
      { title: "Scan credits", value: scanCredits === null ? "-" : `${scanCredits}`, subtitle: "Available" },
      { title: "Completed insights (7d)", value: completedInsightCount.toString(), subtitle: "Available for review" },
      { title: "Draft posts", value: draftPostCount.toString(), subtitle: "Awaiting publish decision" },
    ],
    [completedInsightCount, confirmedVisitCount, consultCredits, draftPostCount, pendingVisitCount, scanCredits],
  );

  if (isDietitian) {
    return (
      <div data-testid="home-page" className="space-y-4 pb-4">
        <PageHeader title="Dietitian Home" subtitle="Review member requests, finalize recommendations, and publish guidance." />

        <Card className="overflow-hidden p-0">
          <div className="relative h-56 overflow-hidden rounded-card sm:h-64">
            <img src={assetUrl("ai-insights-hero.png")} alt="Dietitian workspace" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/18 to-black/10" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/85">Dietitian Workspace</p>
              <h2 className="pr-display mt-2 text-2xl font-semibold sm:text-3xl">Dietitian review and coordination</h2>
              <p className="mt-1 text-sm text-white/85">Manage visit requests, refine AI suggestions, and publish member education.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/app/ai-insights/review" className="pr-btn-primary inline-flex min-h-10 items-center rounded-button px-4 text-sm font-semibold" onClick={() => trackEvent("home_cta_clicked", { target: "ai_review", role: "dietitian" })}>
                  Open AI review
                </Link>
                <Link
                  to="/app/visits"
                  className="inline-flex min-h-10 items-center rounded-button border border-white/35 bg-white/15 px-4 text-sm font-semibold backdrop-blur"
                  onClick={() => trackEvent("home_cta_clicked", { target: "visit_triage", role: "dietitian" })}
                >
                  Open visit triage
                </Link>
              </div>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-24" />
            ))}
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={() => window.location.reload()} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {dietitianCards.map((card) => (
              <Card key={card.title} flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">{card.title}</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{card.value}</p>
                <p className="mt-1 text-sm text-[var(--pr-text-muted)]">{card.subtitle}</p>
              </Card>
            ))}
          </div>
        )}

        <Card>
          <h3 className="text-base font-semibold">Dietitian actions</h3>
          <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Open dedicated tools to manage appointments, insights, and publications.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <Link to="/app/visits" className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3 text-sm font-semibold" onClick={() => trackEvent("home_cta_clicked", { target: "visit_triage", role: "dietitian" })}>
              Visit triage
            </Link>
            <Link to="/app/ai-insights/review" className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3 text-sm font-semibold" onClick={() => trackEvent("home_cta_clicked", { target: "ai_review_console", role: "dietitian" })}>
              AI review console
            </Link>
            <Link to="/app/dietitian-says/mine" className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3 text-sm font-semibold" onClick={() => trackEvent("home_cta_clicked", { target: "author_posts", role: "dietitian" })}>
              Author posts
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="space-y-4 pb-4">
      <PageHeader title="Home" subtitle="Your pantry-first nutrition dashboard." />

      <Card className="overflow-hidden p-0">
        <div className="relative h-56 overflow-hidden rounded-card sm:h-64">
          <img src={assetUrl("home-backdrop.png")} alt="PantryRx backdrop" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/15 to-black/10" />
          <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/85">PantryRx</p>
            <h2 className="pr-display mt-2 text-2xl font-semibold sm:text-3xl">Your personalized nutrition dashboard</h2>
            <p className="mt-1 text-sm text-white/85">Scan pantry shelves, unlock AI insights, and reduce food waste.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/app/scans" className="pr-btn-primary inline-flex min-h-10 items-center rounded-button px-4 text-sm font-semibold" onClick={() => trackEvent("home_cta_clicked", { target: "scan_pantry", role: "member" })}>
                Scan pantry
              </Link>
              <Link
                to="/app/ai-insights"
                className="inline-flex min-h-10 items-center rounded-button border border-white/35 bg-white/15 px-4 text-sm font-semibold backdrop-blur"
                onClick={() => trackEvent("home_cta_clicked", { target: "run_insights", role: "member" })}
              >
                Run insights
              </Link>
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {statCards.map((card) => (
            <Card key={card.title} flat>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">{card.title}</p>
              <p className="pr-display mt-2 text-3xl font-semibold">{card.value}</p>
              <p className="mt-1 text-sm text-[var(--pr-text-muted)]">{card.subtitle}</p>
            </Card>
          ))}
        </div>
      )}

      <Card flat className="border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]">
        <p className="text-sm text-[var(--pr-text-muted)]">
          Credit purchases are available in the PantryRx iOS app only. If credits run low, open iOS to top up.
        </p>
      </Card>

      {loading ? (
        <Skeleton className="h-48" />
      ) : scans.length === 0 ? (
        <EmptyState
          title="No scans yet"
          subtitle="Upload pantry photos to unlock dashboard metrics and AI analysis."
          image={assetUrl("pantry-inventory-bg.png")}
          action={<Link to="/app/scans" className="pr-btn-primary inline-flex min-h-10 items-center px-4 text-sm font-semibold" onClick={() => trackEvent("home_cta_clicked", { target: "start_first_scan", role: "member" })}>Start first scan</Link>}
        />
      ) : (
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold">Recent scans</h3>
              <p className="text-sm text-[var(--pr-text-muted)]">Latest pantry uploads and processing state.</p>
            </div>
            <Link to="/app/scans" className="text-sm font-semibold text-[var(--pr-accent-deep)]">
              View all
            </Link>
          </div>

          {latestScan ? (
            <Link
              to={`/app/scans/${latestScan.id}`}
              className="mt-4 flex items-center justify-between rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] p-4 text-sm"
            >
              <div>
                <p className="font-semibold">Latest scan</p>
                <p className="mt-1 text-[var(--pr-text-muted)]">{formatRelative(latestScan.created_at)}</p>
              </div>
              <Pill tone="accent" label={latestScan.status.toUpperCase()} />
            </Link>
          ) : null}

          <div className="mt-3 space-y-2">
            {scans.slice(0, 4).map((scan) => (
              <Link
                key={scan.id}
                to={`/app/scans/${scan.id}`}
                className="flex items-center justify-between rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
              >
                <span className="truncate">{scanLabel(scan)}</span>
                <span className="text-xs text-[var(--pr-text-muted)]">{formatRelative(scan.created_at)}</span>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
